// import React, { createContext, useState, useContext } from "react";

// export const AgreementContext = createContext();

// export const useAgreementContext = () => {
//   const context = useContext(AgreementContext);
//   if (!context) {
//     throw new Error(
//       "useAgreementContext must be used within an AgreementProvider"
//     );
//   }
//   return context;
// };

// export const AgreementProvider = ({ children }) => {
//   const [agreementData, setAgreementData] = useState({
//     unitId: null,
//     agreementDetails: {},
//     tenants: [],
//     agreementDetails: null,
//     currentFlow: null, // 'create' or 'modify'
//     originalAgreement: null,
//   });

//   const addAgreementDetails = (details) => {
//     setAgreementData((prev) => ({
//       ...prev,
//       unitId: details.unitId,
//       agreementDetails: details,
//       currentFlow: "create",
//     }));
//   };

//   const updateAgreementDetails = (details, originalAgreement = null) => {
//     setAgreementData((prev) => ({
//       ...prev,
//       agreementDetails: details,
//       currentFlow: "modify",
//       originalAgreement,
//     }));
//   };

//   const addTenant = (tenant) => {
//     setAgreementData((prev) => ({
//       ...prev,
//       tenants: [...prev.tenants, tenant],
//     }));
//   };

//   const resetAgreementData = () => {
//     setAgreementData({
//       unitId: null,
//       // agreementDetails: {},
//       tenants: [],
//       agreementDetails: null,
//       currentFlow: null,
//       originalAgreement: null,
//     });
//   };

//   const value = {
//     agreementData,
//     addAgreementDetails,
//     updateAgreementDetails,
//     resetAgreementData,
//     addTenant,
//   };

//   return (
//     <AgreementContext.Provider value={value}>
//       {children}
//     </AgreementContext.Provider>
//   );
// };

// src/Contexts/AgreementContext.jsx

import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect, // 1. Import useEffect
} from "react";
import { useAuth } from "../../Utility/AuthProvider";
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants";

export const AgreementContext = createContext();

export const useAgreementContext = () => {
  const context = useContext(AgreementContext);
  if (!context) {
    throw new Error(
      "useAgreementContext must be used within an AgreementProvider"
    );
  }
  return context;
};

const INITIAL_AGREEMENT_DATA = {
  unitId: null,
  agreementDetails: {},
  tenants: [],
  currentFlow: null,
  originalAgreement: null,
};

export const AgreementProvider = ({ children }) => {
  const { jwt } = useAuth();

  // State for single agreement creation flow
  const [agreementData, setAgreementData] = useState(INITIAL_AGREEMENT_DATA);

  // State for the list of all agreements
  const [allAgreements, setAllAgreements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ===============================================
  // --- CORE DATA FETCHING AND STATE MANAGEMENT ---
  // ===============================================

  // ✅ FIX 2: Create one comprehensive reset function.
  // This will be used on logout to clear ALL data.
  const resetAllAgreementState = useCallback(() => {
    setAgreementData(INITIAL_AGREEMENT_DATA); // Reset the creation form data
    setAllAgreements(null); // Reset the list data
    setLoading(false);
    setError("");
    console.log("AgreementContext: All state has been reset.");
  }, []);

  const fetchAllAgreements = useCallback(async () => {
    // If we are already loading or have data, don't fetch again.
    // The reset function will set `allAgreements` to null to allow re-fetching for a new user.
    if (loading || allAgreements !== null) {
      return;
    }

    if (!jwt) return; // Don't fetch if there's no user

    setLoading(true);
    setError("");
    try {
      const apiUrl = `${paths.agreement.fetch}?active=all`;
      const response = await APICall("GET", apiUrl, undefined, undefined, jwt);

      if (response?.status === "success" && Array.isArray(response.response)) {
        // Processing can be done here or just set the raw data
        setAllAgreements(response.response || []);
      } else {
        setError(response?.message || "Failed to parse agreement data.");
        setAllAgreements([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch agreements");
      setAllAgreements([]);
    } finally {
      setLoading(false);
    }
  }, [jwt, loading, allAgreements]); // ✅ FIX 3: Cleaned dependencies

  // ✅ FIX 1: Add a useEffect to react to user changes (login/logout).
  // This is the most important change.
  useEffect(() => {
    // When the user logs out (jwt becomes null) or changes,
    // we MUST reset the state to prevent data leakage.
    console.log("AgreementContext: JWT changed, resetting state.");
    resetAllAgreementState();
  }, [jwt, resetAllAgreementState]); // This effect runs whenever the user's auth status changes

  // Function to allow a component to force a data refresh within the same session
  const forceRefreshAgreements = useCallback(() => {
    setAllAgreements(null); // Setting to null will allow the fetch function to run again
  }, []);

  // --- Other functions for agreement creation flow (no changes needed) ---
  const addAgreementDetails = (details) => {
    /* ... */
  };
  const updateAgreementDetails = (details, originalAgreement = null) => {
    /* ... */
  };
  const addTenant = (tenant) => {
    /* ... */
  };

  const value = {
    agreementData,
    addAgreementDetails,
    updateAgreementDetails,
    addTenant,
    resetAgreementData: resetAllAgreementState, // Use the new comprehensive reset
    allAgreements,
    loading,
    error,
    fetchAllAgreements,
    forceRefreshAgreements,
  };

  return (
    <AgreementContext.Provider value={value}>
      {children}
    </AgreementContext.Provider>
  );
};
